import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  PrepareApplicationCustomer,
  PrepareApplicationCustomerAccount,
} from "@/models/underwriting";
import { applicationService, type CustomerDetailPatchOperation } from "@/services/ApplicationService";
import { toast } from "sonner";

const TITLES = [
  { id: 1, name: "Mr" }, { id: 2, name: "Mrs" }, { id: 3, name: "Ms" },
  { id: 4, name: "Miss" }, { id: 5, name: "Dr" }, { id: 6, name: "Rev" },
];
const MARITAL_STATUSES = [
  { id: 1, name: "Married" }, { id: 2, name: "Cohabiting" }, { id: 3, name: "Single" },
  { id: 4, name: "Divorced" }, { id: 5, name: "Separated" }, { id: 6, name: "Widowed" },
  { id: 7, name: "Civil partnership" },
];
const LICENCE_TYPES = [
  { id: 1, name: "Full New UK Driving Licence [photocard]" },
  { id: 2, name: "Full UK Driving Licence" }, { id: 4, name: "International Licence" },
  { id: 5, name: "No Licence" }, { id: 6, name: "Provisional UK Driving Licence" },
  { id: 7, name: "EU Licence" },
];
const VEHICLE_TYPES = [
  { id: 1, name: "Car" }, { id: 3, name: "Caravan" }, { id: 5, name: "Bike" }, { id: 9, name: "Van" },
];

interface CustomerDetailsDraft {
  titleId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  maritalStatusId: number;
  drivingLicenceTypeId: number;
  vehicleTypeId: number;
  email: string;
  mobileNumber: string;
  workNumber: string;
  homeNumber: string;
  netMonthlyIncome: string;
  bankName: string;
  nameOnBankAccount: string;
  monthsAtBank: string;
  accountNumber: string;
  sortCode: string;
}

interface CustomerDetailsEditorProps {
  applicationId: number;
  customer: PrepareApplicationCustomer;
  customerAccount: PrepareApplicationCustomerAccount | null;
  vehicleTypeId: number | null;
  onCancel: () => void;
  onSaved: () => void;
}

function dateInputValue(value: string | null | undefined): string {
  return value?.slice(0, 10) ?? "";
}

function getInitialDraft(
  customer: PrepareApplicationCustomer,
  account: PrepareApplicationCustomerAccount | null,
  fallbackVehicleTypeId: number | null
): CustomerDetailsDraft {
  const customerWithVehicleType = customer as PrepareApplicationCustomer & { vehicleTypeId?: number | null };
  return {
    titleId: account?.titleId ?? 0,
    firstName: account?.firstName ?? customer.firstName ?? "",
    middleName: account?.middleNames ?? "",
    lastName: account?.surname ?? customer.lastName ?? "",
    dateOfBirth: dateInputValue(account?.dateOfBirth ?? customer.dateOfBirth),
    maritalStatusId: account?.maritalStatusId ?? customer.maritalStatus?.maritalStatusId ?? 0,
    drivingLicenceTypeId: account?.drvLicenceTypeId ?? customer.drivingLicenceType?.drivingLicenceTypeId ?? 0,
    vehicleTypeId: customerWithVehicleType.vehicleTypeId ?? fallbackVehicleTypeId ?? 0,
    email: account?.emailAddress ?? "",
    mobileNumber: account?.mobilePhone ?? "",
    workNumber: account?.daytimePhone ?? "",
    homeNumber: account?.eveningPhone ?? "",
    netMonthlyIncome: String(account?.netMonthlyIncome ?? customer.bankDetails?.netMonthlyIncome ?? ""),
    bankName: account?.bankName ?? customer.bankDetails?.bankName ?? "",
    nameOnBankAccount: account?.bankAccountName ?? customer.bankDetails?.nameOnBankAccount ?? "",
    monthsAtBank: String(account?.bankMonthsAt ?? customer.bankDetails?.monthsAtBank ?? ""),
    accountNumber: account?.bankAccountNumber ?? customer.bankDetails?.accountNumber ?? "",
    sortCode: account?.bankSortCode ?? customer.bankDetails?.sortCode ?? "",
  };
}

const fieldClassName = "grid gap-1.5";
const labelClassName = "text-[12px] font-medium text-foreground";
const inputClassName = "h-9 text-[12px]";

export function CustomerDetailsEditor({
  applicationId,
  customer,
  customerAccount,
  vehicleTypeId,
  onCancel,
  onSaved,
}: CustomerDetailsEditorProps) {
  const initialDraft = useMemo(
    () => getInitialDraft(customer, customerAccount, vehicleTypeId),
    [customer, customerAccount, vehicleTypeId]
  );
  const [draft, setDraft] = useState(initialDraft);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const isDirty = Object.keys(initialDraft).some((key) => {
    const field = key as keyof CustomerDetailsDraft;
    return draft[field] !== initialDraft[field];
  });

  const setField = <K extends keyof CustomerDetailsDraft>(key: K, value: CustomerDetailsDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!draft.titleId) nextErrors.titleId = "The title field is required.";
    if (!draft.firstName.trim()) nextErrors.firstName = "The first name field is required.";
    if (!draft.lastName.trim()) nextErrors.lastName = "The last name field is required.";
    if (!draft.maritalStatusId) nextErrors.maritalStatusId = "The marital status field is required.";
    if (!draft.drivingLicenceTypeId) nextErrors.drivingLicenceTypeId = "The driving licence type field is required.";
    if (!draft.vehicleTypeId) nextErrors.vehicleTypeId = "The vehicle type field is required.";
    if (!draft.netMonthlyIncome || !/^\d+(\.\d+)?$/.test(draft.netMonthlyIncome)) {
      nextErrors.netMonthlyIncome = "The net monthly income field is required.";
    }
    if (!draft.email.trim()) nextErrors.email = "The email field is required.";
    else if (!/^[\w-.+]+@([\w-]+\.)+[\w-]+$/.test(draft.email)) nextErrors.email = "The email field must be a valid email.";
    if (!draft.dateOfBirth) nextErrors.dateOfBirth = "The date of birth field is required.";
    if (draft.accountNumber && !/^\d{8}$/.test(draft.accountNumber)) nextErrors.accountNumber = "The account number field must be 8 digits long.";
    if (draft.sortCode && !/^\d{6}$/.test(draft.sortCode)) nextErrors.sortCode = "The sort code field must be valid.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const title = TITLES.find((item) => item.id === draft.titleId)!;
    const maritalStatus = MARITAL_STATUSES.find((item) => item.id === draft.maritalStatusId)!;
    const licence = LICENCE_TYPES.find((item) => item.id === draft.drivingLicenceTypeId)!;
    const initial = getInitialDraft(customer, customerAccount, vehicleTypeId);
    const changes: CustomerDetailPatchOperation[] = [];
    const addChange = (key: keyof CustomerDetailsDraft, path: string, value: unknown) => {
      if (draft[key] !== initial[key]) changes.push({ op: "replace", path: `/${path}`, value });
    };

    addChange("titleId", "title", { titleId: title.id, titleName: title.name });
    addChange("firstName", "firstName", draft.firstName.trim());
    addChange("middleName", "middleName", draft.middleName.trim());
    addChange("lastName", "lastName", draft.lastName.trim());
    addChange("dateOfBirth", "dateOfBirth", new Date(`${draft.dateOfBirth}T00:00:00.000Z`).toISOString());
    addChange("maritalStatusId", "maritalStatus", { maritalStatusId: maritalStatus.id, maritalStatusName: maritalStatus.name });
    addChange("drivingLicenceTypeId", "drivingLicenceType", { drivingLicenceTypeId: licence.id, drivingLicenceTypeName: licence.name });
    addChange("vehicleTypeId", "vehicleTypeId", draft.vehicleTypeId);
    addChange("email", "email", draft.email.trim());
    addChange("mobileNumber", "mobileNumber", draft.mobileNumber.trim());
    addChange("workNumber", "workNumber", draft.workNumber.trim());
    addChange("homeNumber", "homeNumber", draft.homeNumber.trim());
    addChange("netMonthlyIncome", "bankDetails/netMonthlyIncome", Number(draft.netMonthlyIncome));
    addChange("bankName", "bankDetails/bankName", draft.bankName.trim());
    addChange("nameOnBankAccount", "bankDetails/nameOnBankAccount", draft.nameOnBankAccount.trim());
    addChange("monthsAtBank", "bankDetails/monthsAtBank", Number(draft.monthsAtBank));
    addChange("accountNumber", "bankDetails/accountNumber", draft.accountNumber);
    addChange("sortCode", "bankDetails/sortCode", draft.sortCode);

    if (!changes.length) {
      onCancel();
      return;
    }
    setSaving(true);
    try {
      await applicationService.updateCustomerDetails(applicationId, changes);
      toast.success("Customer details updated successfully.");
      onSaved();
    } catch {
      toast.error("Could not update customer details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key: keyof CustomerDetailsDraft) => errors[key]
    ? <p className="text-[11px] text-rose-600" role="alert">{errors[key]}</p>
    : null;
  const selectField = (
    key: "titleId" | "maritalStatusId" | "drivingLicenceTypeId" | "vehicleTypeId",
    label: string,
    options: { id: number; name: string }[]
  ) => (
    <div className={fieldClassName}>
      <Label className={labelClassName}>{label}</Label>
      <Select value={draft[key] ? String(draft[key]) : ""} onValueChange={(value) => setField(key, Number(value))}>
        <SelectTrigger aria-label={label} aria-invalid={Boolean(errors[key])} className="h-9 text-[12px]"><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>{options.map((option) => <SelectItem key={option.id} value={String(option.id)}>{option.name}</SelectItem>)}</SelectContent>
      </Select>
      {fieldError(key)}
    </div>
  );
  const textField = (
    key: keyof CustomerDetailsDraft,
    label: string,
    type = "text"
  ) => (
    <div className={fieldClassName}>
      <Label htmlFor={`customer-${key}`} className={labelClassName}>{label}</Label>
      <Input
        id={`customer-${key}`}
        className={inputClassName}
        type={type}
        value={draft[key]}
        aria-invalid={Boolean(errors[key])}
        max={type === "date" ? new Date().toISOString().slice(0, 10) : undefined}
        onChange={(event) => setField(key, event.target.value as never)}
      />
      {fieldError(key)}
    </div>
  );

  return (
    <div className="grid gap-5 p-4">
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="grid content-start gap-4" aria-labelledby="customer-details-heading">
          <h3 id="customer-details-heading" className="text-sm font-semibold">Customer Details</h3>
          {selectField("titleId", "Title", TITLES)}
          <div className="grid gap-4 sm:grid-cols-2">
            {textField("firstName", "First Name")}
            {textField("middleName", "Middle Name(s)")}
          </div>
          {textField("lastName", "Last Name")}
          {textField("dateOfBirth", "Date of Birth", "date")}
          {selectField("maritalStatusId", "Marital Status", MARITAL_STATUSES)}
          {selectField("drivingLicenceTypeId", "Driving Licence Type", LICENCE_TYPES)}
          <h3 className="border-t border-border pt-4 text-sm font-semibold">Application Details</h3>
          {selectField("vehicleTypeId", "Vehicle Type", VEHICLE_TYPES)}
          {textField("netMonthlyIncome", "Net Monthly Income", "number")}
        </section>
        <section className="grid content-start gap-4" aria-labelledby="contact-details-heading">
          <h3 id="contact-details-heading" className="text-sm font-semibold">Contact Details</h3>
          {textField("email", "Email Address", "email")}
          {textField("mobileNumber", "Mobile No.")}
          {textField("workNumber", "Daytime Phone")}
          {textField("homeNumber", "Evening Phone")}
          <h3 className="border-t border-border pt-4 text-sm font-semibold">Bank Details</h3>
          {textField("bankName", "Bank Name")}
          {textField("nameOnBankAccount", "Name on Bank Account")}
          {textField("monthsAtBank", "Months at Bank", "number")}
          <div className="grid gap-4 sm:grid-cols-2">
            {textField("accountNumber", "Account No.")}
            {textField("sortCode", "Sort Code")}
          </div>
        </section>
      </div>
      <div className="flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" disabled={saving} onClick={onCancel}>Cancel</Button>
        <Button disabled={saving || !isDirty} onClick={handleSave}>
          {saving && <Loader2 className="size-4 animate-spin" />}
          {saving ? "Saving..." : "Update Details"}
        </Button>
      </div>
    </div>
  );
}